import React, { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { useTranslation } from 'react-i18next';
import U2000ActionBtn from '../../u2000Core/components/buttons/U2000ActionBtn';
import U2000BasicDialog from '../../u2000Core/components/dialog/U2000BasicDialog';
import U2000Label from '../../u2000Core/components/formControls/label/U2000Label';
import U2000Panel from '../../u2000Core/components/layout/U2000Panel';
import { NS } from '../U2106';
import { useU2106MfaHandlers } from '../hooks/useU2106MfaHandlers';

type EnrollStep = 'idle' | 'scan' | 'backup';
type ProofMode = 'disable' | 'regenerate' | null;

type U2106MfaPanelProps = {
  /** État initial depuis GET /profile (optionnel). */
  initialEnabled?: boolean;
  initialEnrolled?: boolean;
};

const inputClass =
  'w-full rounded-lg border border-gray-300 bg-surface px-4 py-2.5 text-sm text-gray-900 focus:outline-hidden focus:ring-2 focus:ring-blue-500/20';

/** BL-0413 — section Double authentification (opt-in TOTP). */
const U2106MfaPanel: React.FC<U2106MfaPanelProps> = ({
  initialEnabled = false,
  initialEnrolled = false,
}) => {
  const { t } = useTranslation(NS);
  const {
    loading,
    status,
    loadStatus,
    enrollStart,
    enrollConfirm,
    disable,
    regenerateBackupCodes,
  } = useU2106MfaHandlers();

  const [enrollStep, setEnrollStep] = useState<EnrollStep>('idle');
  const [otpauthUri, setOtpauthUri] = useState('');
  const [manualSecret, setManualSecret] = useState('');
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [confirmCode, setConfirmCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [proofMode, setProofMode] = useState<ProofMode>(null);
  const [proofPassword, setProofPassword] = useState('');
  const [proofTotp, setProofTotp] = useState('');
  const [localEnabled, setLocalEnabled] = useState(initialEnabled);
  const [localEnrolled, setLocalEnrolled] = useState(initialEnrolled);

  useEffect(() => {
    let cancelled = false;
    void loadStatus().then((result) => {
      if (!cancelled && result) {
        setLocalEnabled(result.enabled);
        setLocalEnrolled(result.enrolled);
      }
    });
    return () => {
      cancelled = true;
    };
    // Charge une fois au montage ; loadStatus est stable via useCallback.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only
  }, []);

  useEffect(() => {
    if (proofMode !== null) {
      setProofPassword('');
      setProofTotp('');
    }
  }, [proofMode]);

  useEffect(() => {
    if (status) {
      setLocalEnabled(status.enabled);
      setLocalEnrolled(status.enrolled);
    }
  }, [status]);

  useEffect(() => {
    if (!otpauthUri) {
      setQrDataUrl('');
      return;
    }
    let cancelled = false;
    void QRCode.toDataURL(otpauthUri, { width: 220, margin: 2 }).then((url) => {
      if (!cancelled) {
        setQrDataUrl(url);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [otpauthUri]);

  if (status?.applicable === false) {
    return null;
  }

  const enabled = status?.enabled ?? localEnabled;
  const enrolled = status?.enrolled ?? localEnrolled;

  const resetEnroll = () => {
    setEnrollStep('idle');
    setOtpauthUri('');
    setManualSecret('');
    setQrDataUrl('');
    setConfirmCode('');
  };

  const resetProof = () => {
    setProofMode(null);
    setProofPassword('');
    setProofTotp('');
  };

  const handleStartEnroll = async () => {
    const result = await enrollStart();
    if (!result) {
      return;
    }
    setOtpauthUri(result.otpauthUri);
    setManualSecret(result.manualSecret);
    setConfirmCode('');
    setEnrollStep('scan');
  };

  const handleConfirmEnroll = async () => {
    const codes = await enrollConfirm(confirmCode);
    if (!codes) {
      return;
    }
    setBackupCodes(codes);
    setEnrollStep('backup');
    setLocalEnabled(true);
    setLocalEnrolled(true);
  };

  const handleCloseBackup = () => {
    resetEnroll();
    setBackupCodes([]);
  };

  const handleProofSubmit = async () => {
    const password = proofPassword.trim();
    const totpCode = proofTotp.trim();
    if (!password && !totpCode) {
      return;
    }
    // Un seul champ dans le body : TOTP prioritaire si les deux sont remplis.
    const payload: { password?: string; totpCode?: string } = totpCode
      ? { totpCode }
      : { password };
    if (proofMode === 'disable') {
      const ok = await disable(payload);
      if (ok) {
        setLocalEnabled(false);
        setLocalEnrolled(false);
        resetProof();
      }
      return;
    }
    if (proofMode === 'regenerate') {
      const codes = await regenerateBackupCodes(payload);
      if (codes) {
        resetProof();
        setBackupCodes(codes);
        setEnrollStep('backup');
      }
    }
  };

  const copyBackupCodes = async () => {
    try {
      await navigator.clipboard.writeText(backupCodes.join('\n'));
    } catch {
      // ignore clipboard failures
    }
  };

  const downloadBackupCodes = () => {
    const blob = new Blob([backupCodes.join('\n')], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'mon-arpenteur-backup-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <U2000Panel title={t('myAccount.mfa.title', { ns: NS })} titleAlign="left" className="w-full">
        <div className="mt-3 space-y-4 px-2">
          <p className="text-sm text-gray-600">
            {enabled
              ? t('myAccount.mfa.statusOn', { ns: NS })
              : t('myAccount.mfa.statusOff', { ns: NS })}
            {enrolled && enabled ? (
              <span className="ml-2 text-xs text-gray-500">
                ({t('myAccount.mfa.enrolledHint', { ns: NS })})
              </span>
            ) : null}
          </p>

          {!enabled ? (
            <div className="flex flex-wrap gap-3">
              <U2000ActionBtn
                handleClick={() => void handleStartEnroll()}
                disabled={loading || enrollStep === 'scan'}
                label={t('myAccount.mfa.enable', { ns: NS })}
              />
            </div>
          ) : (
            <div className="flex flex-wrap gap-3">
              <U2000ActionBtn
                handleClick={() => setProofMode('regenerate')}
                disabled={loading}
                label={t('myAccount.mfa.regenerateBackup', { ns: NS })}
                variant="secondary"
              />
              <U2000ActionBtn
                handleClick={() => setProofMode('disable')}
                disabled={loading}
                label={t('myAccount.mfa.disable', { ns: NS })}
                className="bg-red-600! hover:bg-red-700!"
              />
            </div>
          )}

          {enrollStep === 'scan' ? (
            <div className="space-y-4 rounded-lg border border-gray-200 bg-muted/40 p-4">
              <p className="text-sm text-gray-700">{t('myAccount.mfa.scanQr', { ns: NS })}</p>
              {qrDataUrl ? (
                <img
                  src={qrDataUrl}
                  alt={t('myAccount.mfa.qrAlt', { ns: NS })}
                  className="mx-auto h-[220px] w-[220px] rounded-lg border border-gray-200 bg-white p-2"
                />
              ) : (
                <p className="text-center text-sm text-gray-500">{t('myAccount.mfa.qrLoading', { ns: NS })}</p>
              )}
              <div className="space-y-1.5">
                <U2000Label label={t('myAccount.mfa.manualSecret', { ns: NS })} required={false} />
                <input
                  type="text"
                  readOnly
                  value={manualSecret}
                  className={`${inputClass} font-mono tracking-wider`}
                  aria-label={t('myAccount.mfa.manualSecret', { ns: NS })}
                />
              </div>
              <div className="space-y-1.5">
                <U2000Label label={t('myAccount.mfa.confirmCode', { ns: NS })} required />
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={confirmCode}
                  onChange={(e) => setConfirmCode(e.target.value)}
                  className={inputClass}
                  maxLength={12}
                  placeholder="000000"
                />
              </div>
              <div className="flex flex-wrap gap-3">
                <U2000ActionBtn
                  handleClick={() => void handleConfirmEnroll()}
                  disabled={loading || confirmCode.trim().length < 6}
                  label={t('myAccount.mfa.confirm', { ns: NS })}
                />
                <button
                  type="button"
                  className="text-sm font-medium text-gray-600 underline hover:text-gray-900"
                  onClick={resetEnroll}
                  disabled={loading}
                >
                  {t('myAccount.mfa.cancel', { ns: NS })}
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </U2000Panel>

      <U2000BasicDialog
        isOpen={proofMode !== null}
        onClose={() => {
          if (!loading) {
            resetProof();
          }
        }}
        title={
          proofMode === 'disable'
            ? t('myAccount.mfa.disable', { ns: NS })
            : t('myAccount.mfa.regenerateBackup', { ns: NS })
        }
        width="small"
      >
        <div className="space-y-4 p-2">
          <p className="text-sm text-gray-600">{t('myAccount.mfa.proofHint', { ns: NS })}</p>
          <div className="space-y-1.5">
            <U2000Label label={t('myAccount.mfa.confirmCode', { ns: NS })} required={false} />
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              name="mfa-proof-totp"
              value={proofTotp}
              onChange={(e) => {
                setProofTotp(e.target.value);
                if (e.target.value) {
                  setProofPassword('');
                }
              }}
              className={inputClass}
              maxLength={12}
              placeholder="000000"
            />
          </div>
          <p className="text-center text-xs text-gray-500">{t('myAccount.mfa.proofOr', { ns: NS })}</p>
          <div className="space-y-1.5">
            <U2000Label label={t('myAccount.mfa.password', { ns: NS })} required={false} />
            <input
              type="password"
              autoComplete="new-password"
              name="mfa-proof-password"
              readOnly
              onFocus={(e) => e.currentTarget.removeAttribute('readonly')}
              value={proofPassword}
              onChange={(e) => {
                setProofPassword(e.target.value);
                if (e.target.value) {
                  setProofTotp('');
                }
              }}
              className={inputClass}
            />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              className="rounded-lg px-3 py-2 text-sm text-gray-600 hover:bg-muted"
              onClick={resetProof}
              disabled={loading}
            >
              {t('myAccount.mfa.cancel', { ns: NS })}
            </button>
            <U2000ActionBtn
              handleClick={() => void handleProofSubmit()}
              disabled={loading || (!proofPassword.trim() && !proofTotp.trim())}
              label={t('myAccount.mfa.confirm', { ns: NS })}
            />
          </div>
        </div>
      </U2000BasicDialog>

      <U2000BasicDialog
        isOpen={enrollStep === 'backup' && backupCodes.length > 0}
        onClose={() => {
          if (!loading) {
            handleCloseBackup();
          }
        }}
        title={t('myAccount.mfa.backupCodesTitle', { ns: NS })}
        width="medium"
      >
        <div className="space-y-4 p-2">
          <p className="text-sm text-amber-800">{t('myAccount.mfa.backupCodesOnce', { ns: NS })}</p>
          <ul className="grid grid-cols-1 gap-2 rounded-lg border border-gray-200 bg-muted/30 p-3 font-mono text-sm sm:grid-cols-2">
            {backupCodes.map((code) => (
              <li key={code}>{code}</li>
            ))}
          </ul>
          <div className="flex flex-wrap gap-3">
            <U2000ActionBtn
              handleClick={() => void copyBackupCodes()}
              label={t('myAccount.mfa.copyCodes', { ns: NS })}
              variant="secondary"
            />
            <U2000ActionBtn
              handleClick={downloadBackupCodes}
              label={t('myAccount.mfa.downloadCodes', { ns: NS })}
              variant="secondary"
            />
            <U2000ActionBtn
              handleClick={handleCloseBackup}
              label={t('myAccount.mfa.backupCodesAck', { ns: NS })}
            />
          </div>
        </div>
      </U2000BasicDialog>
    </>
  );
};

export default U2106MfaPanel;
